import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import './App.css';
import { AboutPage, HeroLandingPage, MarketingHomePage, MarketingRoute, MembershipPage, WaitlistPage, WaitlistThankYouPage, WaitlistDepositPage, AssessmentsPage, FaqPage } from './marketing';
import ScrollToTop from './marketing/layout/ScrollToTop';
import MarketingShell from './marketing/layout/MarketingShell';
import LoginPage from './unAuth/LoginPage';
import MemberHomePage from './pages/MemberHomePage';
import MemberTestingPage from './pages/MemberTestingPage';
import DailyBitePage from './pages/DailyBitePage';
import PartnerCheerPage from './pages/PartnerCheerPage';
import DailyBiteSettingsPage from './pages/DailyBiteSettingsPage';
import Onboarding from './Auth/Onboarding';
import PeptideSurvey from './pages/PeptideSurvey';
import PeptideSurveyThankYou from './pages/PeptideSurveyThankYou';
import EnrollmentPage from './pages/EnrollmentPage';
import CampaignLandingPage from './pages/CampaignLandingPage';
import ReferralLandingPage from './pages/ReferralLandingPage';
import BookThankYouPage from './pages/BookThankYouPage';
import ClinicalHistoryBookPage from './pages/ClinicalHistoryBookPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import HealthHistoryPage from './pages/HealthHistoryPage';
import MelioIntelligencePage from './pages/MelioIntelligencePage';
import MenstrualCyclePage from './pages/MenstrualCyclePage';
import BodyMapPage from './pages/BodyMapPage';
import BodyMapIssuePage from './pages/BodyMapIssuePage';
import { BODY_MAP_ENABLED, LANDING_PAGE, PUBLIC_SITE } from './config/featureFlags';
import ProfilePage from './pages/ProfilePage';
import WearableMetricDetailPage from './pages/WearableMetricDetailPage';
import MessagesPage from './pages/MessagesPage';
import NotFoundPage from './pages/NotFoundPage';
import MemberResultsPage from './pages/MemberResultsPage';
import ReportsPage from './pages/ReportsPage';
import QuarterlyReportDetailPage from './pages/QuarterlyReportDetailPage';
import YourPlanPage from './pages/YourPlanPage';
import FeedbackChatPage from './pages/FeedbackChatPage';
import { AuthProvider } from './context/AuthContext';
import RequireAuth from './components/RequireAuth';
import UnAuthRoute from './components/UnAuthRoute';
import MemberProtectedLayout from './components/layout/MemberProtectedLayout';
import GatedLayout from './components/layout/GatedLayout';
import ConsentBanner from './components/ConsentBanner';
import Logout from './Logout';
import './firebase';
// Self-registers the consent-gated GA4 loader against the analytics consent
// store; no analytics loads until the visitor accepts (SEC-6).
import './utils/loadAnalytics';

// isGatedHost now lives with the gate itself, in components/layout/GatedLayout.

function App() {
  const appContent = (
    <Router>
      <div className="App">
        <ScrollToTop />
        <ConsentBanner />
        <Routes>
          {/* ── PUBLIC (ungated) ───────────────────────────────────────────
              The marketing site and the waitlist sit outside the site-access
              gate so campaign URLs actually work: a podcast host reads
              melio.health/c/rangan aloud, and that listener must not meet a
              password wall. The product — enrolment, auth and every member
              route — stays gated below.

              Anything added here is world-readable in production. It must
              contain no PHI and no member-identifying data. */}

          {/* Site root. On a login-first build ('/' → /login) the marketing home
              page stays reachable at /practice, so the header logo, the
              "back to home" buttons and the other public pages still have a
              real destination. See config/featureFlags. */}
          {/* W-13 — hero mode. The public site is one screen: '/' is the hero
              and the four content pages fold into it.

              REDIRECT, NOT REMOVE. Links to /membership and /assessments are
              already out in the world (they were the CTA targets until this
              week), and search results outlive a deploy. A redirect means every
              one of them lands on the hero and the waitlist; a 404 or a
              password box would spend traffic we are about to pay for. The
              pages themselves are untouched and come back with the flag. */}
          {/* ORDER MATTERS, and it is the reverse of what it was. LANDING_PAGE
              answers "who is this build for" and PUBLIC_SITE answers "how much
              of the public site is published", so the first question has to be
              asked first. Testing 'hero' first meant a login-first build could
              be silently overruled: staging set LANDING_PAGE=login, inherited
              PUBLIC_SITE=hero from .env.production through CRA's dotenv
              back-fill, and served the public hero to an environment that holds
              real member data and has no link to /login anywhere on it.

              Production is unaffected either way — it sets
              LANDING_PAGE=marketing, so '/' still falls through to the hero. */}
          {/* The hero pages share one mounted chrome (MarketingShell) so a
              navigation between them dissolves the photograph and eases the
              nav instead of remounting both. The shell carries the
              signed-in redirect (MarketingRoute) for all of them. */}
          <Route element={<MarketingShell />}>
            <Route
              path="/"
              element={
                LANDING_PAGE === 'login'
                  ? <Navigate to="/login" replace />
                  : PUBLIC_SITE === 'hero'
                    ? <HeroLandingPage />
                    : <MarketingHomePage />
              }
            />
            {/* /practice stays a real alias: on a login-first build '/' goes to
                /login and this is the only way to reach the marketing site. */}
            <Route
              path="/practice"
              element={PUBLIC_SITE === 'hero' ? <Navigate to="/" replace /> : <MarketingHomePage />}
            />
            <Route
              path="/about"
              element={PUBLIC_SITE === 'hero' ? <Navigate to="/" replace /> : <AboutPage />}
            />
            <Route
              path="/membership"
              element={PUBLIC_SITE === 'hero' ? <Navigate to="/" replace /> : <MembershipPage />}
            />
            <Route
              path="/assessments"
              element={PUBLIC_SITE === 'hero' ? <Navigate to="/" replace /> : <AssessmentsPage />}
            />
          </Route>
          <Route
            path="/faq"
            element={PUBLIC_SITE === 'hero' ? <Navigate to="/" replace /> : <MarketingRoute><FaqPage /></MarketingRoute>}
          />
          <Route path="/waitlist" element={<MarketingRoute><WaitlistPage /></MarketingRoute>} />
          <Route path="/waitlist/thank-you" element={<MarketingRoute><WaitlistThankYouPage /></MarketingRoute>} />
          <Route path="/waitlist/deposit" element={<MarketingRoute><WaitlistDepositPage /></MarketingRoute>} />
          <Route path="/c/:slug" element={<UnAuthRoute><CampaignLandingPage /></UnAuthRoute>} />
          {/* Member REFERRAL landing page. Not to be confused with an
              enrolment invitation — different system, similar word. */}
          <Route path="/invite/:code" element={<UnAuthRoute><ReferralLandingPage /></UnAuthRoute>} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />

          {/* WHY /login IS PUBLIC AND /signup IS NOT.
              Melio has not opened, so there must be no way to create an account
              from the marketing site - /signup redirects to the waitlist, which
              is the only front door pre-launch.

              /login is a different question. Production holds real accounts with
              real observations behind them, and leaving login inside the site
              gate would mean the people whose results those are could not reach
              their own data without a shared password. So the route stays
              openly reachable; it is simply no longer linked from the marketing
              chrome (see PublicMarketingLayout). Not advertised is not the same
              as not available, and only one of those is defensible for someone
              else's health records. */}
          <Route path="/login" element={<UnAuthRoute><LoginPage /></UnAuthRoute>} />
          <Route path="/signup" element={<Navigate to="/waitlist" replace />} />

          {/* ── GATED ──────────────────────────────────────────────────────
              Everything below renders inside SiteAccessGate on a gated host.
              GatedLayout is a layout route on purpose: a pathname check
              outside the Router would not re-run on client-side navigation,
              so a visitor could SPA-navigate in from a public page. */}
          <Route element={<GatedLayout />}>
          <Route path="/survey" element={<PeptideSurvey />} />
          <Route path="/survey/thank-you" element={<PeptideSurveyThankYou />} />
          <Route path="/enrollment" element={<EnrollmentPage />} />
          <Route path="/book" element={<EnrollmentPage />} />
          <Route path="/enrollment/thank-you" element={<BookThankYouPage />} />
          <Route path="/book/thank-you" element={<BookThankYouPage />} />
          <Route path="/book/clinical-history" element={
            <RequireAuth>
              <ClinicalHistoryBookPage />
            </RequireAuth>
          } />
          <Route path="/cheer/:linkId" element={<PartnerCheerPage />} />
          <Route element={<MemberProtectedLayout />}>
            <Route path="/home" element={<MemberHomePage />} />
            <Route path="/dashboard" element={<Navigate to="/home" replace />} />
            <Route path="/welcome" element={<Navigate to="/home" replace />} />
            {/* /results is the destination now, not a waypoint.
                It redirected to /reports once (documents, not results), then to
                /intelligence?tab=results (a query param on a tab inside a page
                named after something else). Both were answers to "where are my
                results" that a member could not have guessed. The old URLs
                redirect here so existing bookmarks and any link already emailed
                to a member keep working. */}
            <Route path="/results" element={<MemberResultsPage />} />
            <Route path="/uploads" element={<Navigate to="/results" replace />} />
            <Route path="/family-tree" element={<Navigate to="/history" replace />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/history" element={<HealthHistoryPage />} />
            {/* Body Map is gated off for members (see config/featureFlags).
                Redirect rather than omit, so a bookmarked or typed /body-map
                URL lands on Home instead of the not-found page. */}
            <Route
              path="/body-map"
              element={BODY_MAP_ENABLED ? <BodyMapPage /> : <Navigate to="/home" replace />}
            />
            <Route
              path="/body-map/issues/:issueId"
              element={BODY_MAP_ENABLED ? <BodyMapIssuePage /> : <Navigate to="/home" replace />}
            />
            <Route path="/intelligence" element={<MelioIntelligencePage />} />
            <Route path="/cycle" element={<MenstrualCyclePage />} />
            <Route path="/intelligence/wearables/:metricId" element={<WearableMetricDetailPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            {/* Ask Melio — test-cohort feedback. The page redirects to /home
                when the feature is off, and the server gate refuses outside a
                non-production project regardless. */}
            <Route path="/feedback" element={<FeedbackChatPage />} />
            <Route path="/testing" element={<MemberTestingPage />} />
            <Route path="/today" element={<DailyBitePage />} />
            <Route path="/today/settings" element={<DailyBiteSettingsPage />} />
            <Route path="/reports" element={<ReportsPage />} />
            <Route path="/reports/:reportId" element={<QuarterlyReportDetailPage />} />
            <Route path="/plan" element={<YourPlanPage />} />
            {/* /homehub and /detail/:id were routed here from the legacy social
                feed. Both were dead ends inside the member shell: HomeHub
                rendered "Welcome 👋" and linked to /discover and /feed, neither
                of which is a registered route, and DetailPage read
                location.state.item so any refresh or direct visit rendered
                "Item not found". Unrouted — they now fall through to the 404,
                which offers real member destinations. */}
          </Route>

          <Route path="/logout" element={<RequireAuth><Logout /></RequireAuth>} />
          <Route path="/health-survey" element={<RequireAuth><HealthHistoryPage /></RequireAuth>} />
          <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
          <Route path="/u/:uid" element={<RequireAuth><HealthHistoryPage /></RequireAuth>} />
          {/* Unknown paths stay behind the gate: a typo must not become a way
              to probe which routes exist on an unreleased product. */}
          <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Routes>
      </div>
    </Router>
  );

  return <AuthProvider>{appContent}</AuthProvider>;
}

export default App;